import { type FC, useMemo } from 'react';
import { Box, Flex, Link, useBreakpointValue } from '@chakra-ui/react';
import parse from 'html-react-parser';
import { FormattedMessage } from 'react-intl';
import { CalendarIcon } from '../../../../../components/icons/Icons';
import { StorageKey } from '../../../../../hooks/model/StorageKey';
import { useCookieStorage } from '../../../../../hooks/useCookieStorage';
import { Text } from '../../../../../ui-components';
import { buildPDPUrl } from '../../../../../utils/product-utils';
import { BloomreachProductType } from '../../../hooks/use-search';
import { getProductTileId } from '../../../utils/search-page-utils';
import { PlpProductPrice } from '../../PlpProductPrice';
import { ProductImage } from '../../product-image';
import type { TextSize } from '../../../../../ui-components';
import type { SearchProductTileProps } from '../../search-listing/SearchListing';

export const ProductTileCulinary: FC<SearchProductTileProps> = ({
    onClick,
    loading,
    fetchPriority,
    ...product
}) => {
    const culinaryClassPreferredStore = useCookieStorage(StorageKey.CULINARY_CLASS_PREFERRED_STORE);
    const url = useMemo<string>(() => {
        const { id: productId } = product;
        const pdpUrl = buildPDPUrl(product);
        const urlParams = new URLSearchParams(pdpUrl.includes('?') ? pdpUrl.split('?')[1] : '');
        const storeIdParam = `dwvar_${productId}_storeId`;
        const storeId = urlParams.get(storeIdParam);
        if (!storeId) {
            const preferredStore = culinaryClassPreferredStore.data;
            urlParams.set(storeIdParam, preferredStore ?? '999');
        }
        return `${pdpUrl.split('?')[0]}?${urlParams.toString()}`;
    }, [product, culinaryClassPreferredStore.data]);

    const nameTextSize = useBreakpointValue<TextSize>({ base: 'small', md: 'base' }) ?? 'small';

    const { productType } = product;
    if (productType !== BloomreachProductType.culinary) {
        return null;
    }

    const { id, name, c_imageUrl, cuisineType, searchPrices } = product;

    const componentId = getProductTileId(id);

    return (
        <Link
            id={componentId}
            data-testid={componentId}
            href={url}
            _hover={{ textDecoration: 'none' }}
            w="100%"
            onClick={onClick}
        >
            <Flex direction="column" gap={3}>
                <ProductImage
                    id={id}
                    name={name}
                    imageUrl={c_imageUrl}
                    loading={loading}
                    fetchPriority={fetchPriority}
                />

                <div className="product-tile-info flex flex-col gap-3">
                    {cuisineType && (
                        <Box
                            paddingInline={2}
                            borderWidth={1}
                            borderColor="slt-pepper.200"
                            width="fit-content"
                            bgColor="slt-beige.400"
                            lineHeight="22px"
                        >
                            <Text
                                id={`culinary-${id}-cuisine-type`}
                                size="xs"
                                style="bold"
                                textTransform="uppercase"
                            >
                                {cuisineType}
                            </Text>
                        </Box>
                    )}

                    <Flex direction="column" gap={1}>
                        <Text id={`culinary-${id}-name`} size={nameTextSize}>
                            {parse(name)}
                        </Text>

                        <Flex alignItems={{ base: 'start', lg: 'center' }} gap={0.5}>
                            <Box data-testid={`culinary-${id}-calendar-icon`} padding={1}>
                                <CalendarIcon />
                            </Box>

                            <Text id={`culinary-${id}-helper-text`} size="xs">
                                <FormattedMessage
                                    id="multiple-dates-times-locations-available"
                                    defaultMessage="Multiple dates, times and locations available"
                                />
                            </Text>
                        </Flex>
                    </Flex>

                    <PlpProductPrice id={id} isSingleVariant searchPrices={searchPrices} />
                </div>
            </Flex>
        </Link>
    );
};
